import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import RowActionButton, { RowActions } from "@/components/RowActionButton";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, PencilSimple, Trash, ArrowDown, ArrowUp } from "@phosphor-icons/react";
import CategoryDialog from "@/components/CategoryDialog";
import DeleteCategoryDialog from "@/components/DeleteCategoryDialog";
import { CategoryIcon } from "@/lib/categoryIcons";

const SECTIONS = [
  {
    kind: "expense",
    title: "Expense categories",
    blurb: "Where your money goes.",
    icon: ArrowDown,
    accent: "text-expense",
  },
  {
    kind: "income",
    title: "Income categories",
    blurb: "Where your money comes from.",
    icon: ArrowUp,
    accent: "text-income",
  },
];

function CategoryRow({ category, onEdit, onDelete }) {
  const used = category.transaction_count || 0;
  return (
    <div
      className="flex items-center justify-between gap-3 border border-border rounded-md p-3 group"
      data-testid={`category-${category.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-9 h-9 shrink-0 rounded-md flex items-center justify-center"
          style={{ background: `${category.color}1f`, color: category.color }}
        >
          <CategoryIcon category={category} size={18} />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">{category.name}</p>
          <p className="text-xs text-muted-foreground">
            {used ? `${used} transaction${used === 1 ? "" : "s"}` : "Not used yet"}
          </p>
        </div>
      </div>
      <RowActions className="shrink-0">
        <RowActionButton
          onClick={() => onEdit(category)}
          aria-label={`Edit ${category.name}`}
          data-testid={`edit-category-${category.id}`}
        >
          <PencilSimple size={16} />
        </RowActionButton>
        <RowActionButton
          danger
          onClick={() => onDelete(category)}
          aria-label={`Delete ${category.name}`}
          data-testid={`delete-category-${category.id}`}
        >
          <Trash size={16} />
        </RowActionButton>
      </RowActions>
    </div>
  );
}

export default function Categories() {
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formKind, setFormKind] = useState("expense");
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/categories");
      setCategories({ income: res.data.income || [], expense: res.data.expense || [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const all = [...categories.expense, ...categories.income];
  const total = all.length;

  const openNew = (kind) => { setEditing(null); setFormKind(kind); setFormOpen(true); };
  const openEdit = (category) => { setEditing(category); setFormOpen(true); };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tighter">Categories</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {loading ? "Loading…" : `${total} categor${total === 1 ? "y" : "ies"} — used to file every transaction.`}
          </p>
        </div>
        <Button
          size="sm"
          className="bg-income text-black hover:bg-income/90 font-semibold gap-1.5 self-start sm:self-auto"
          onClick={() => openNew("expense")}
          data-testid="add-category-btn"
        >
          <Plus size={15} weight="bold" /> New category
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 bg-card" />)}
        </div>
      ) : (
        SECTIONS.map((section) => {
          const rows = categories[section.kind] || [];
          return (
            <Card
              key={section.kind}
              className="bg-card border-border p-5 rounded-lg"
              data-testid={`categories-${section.kind}`}
            >
              <div className="flex items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-2">
                  <section.icon size={18} weight="bold" className={section.accent} />
                  <div>
                    <h3 className="font-heading font-bold tracking-tight">{section.title}</h3>
                    <p className="text-xs text-muted-foreground">{section.blurb}</p>
                  </div>
                </div>
                <Button
                  size="sm" variant="outline" className="border-border gap-1.5 shrink-0"
                  onClick={() => openNew(section.kind)}
                  data-testid={`add-${section.kind}-category-btn`}
                >
                  <Plus size={15} /> Add
                </Button>
              </div>

              {rows.length ? (
                <div className="space-y-2">
                  {rows.map((c) => (
                    <CategoryRow key={c.id} category={c} onEdit={openEdit} onDelete={setDeleting} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No {section.kind} categories yet.
                </p>
              )}
            </Card>
          );
        })
      )}

      <CategoryDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        defaultKind={formKind}
        onSaved={load}
      />

      <DeleteCategoryDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        category={deleting}
        categories={all}
        onDeleted={load}
      />
    </div>
  );
}
